import { parseISO, subMonths } from "date-fns";
import { asUTCTime, currentDate } from "../../../lib/dateUtils";
import { TableColumns, TableFilters } from "../../shared/crud/list";
import { NumberRenderer, StringRenderer } from "../../shared/renderers/table";
import { useIntl } from "react-intl";
import { useMemo } from "react";
import { dateFilter } from "../../shared/crud/filter-widgets";
import { EventEntity } from "../domain/event.entity";
import { GetFiltersRequestProps } from "../infrastructure/eventQueries.api";
import { reportQueriesMessages } from "../messages";
import { formatMessage } from "../../../lib/intlUtils";

interface ReportQueriesFilters {
  period: string;
}

const defaultRequest = {
  startDate: `${asUTCTime(subMonths(currentDate(), 1), "yyyy-MM-dd")}T00:00:00Z`,
  endDate: `${asUTCTime(currentDate(), "yyyy-MM-dd")}T23:59:59Z`,
};

export function generateEventHeaders(): TableColumns<EventEntity> {
  return {
    id: {
      renderer: StringRenderer,
      width: 200,
    },
    userId: {
      renderer: StringRenderer,
      width: 200,
    },
    sessionId: {
      renderer: StringRenderer,
      width: 200,
    },
    createdAt: {
      renderer: StringRenderer,
      width: 200,
    },
    propId: {
      renderer: StringRenderer,
      width: 125,
    },
    propMultId: {
      renderer: StringRenderer,
      width: 125,
    },
    propCountry: {
      renderer: StringRenderer,
      width: 125,
    },
    propCity: {
      renderer: StringRenderer,
      width: 125,
    },
    bookDate: {
      renderer: StringRenderer,
      width: 150,
    },
    bookWeekday: {
      renderer: StringRenderer,
      width: 125,
    },
    bookTime: {
      renderer: StringRenderer,
      width: 125,
    },
    bookTimezone: {
      renderer: StringRenderer,
      width: 150,
    },
    bookGeo: {
      renderer: StringRenderer,
      width: 125,
    },
    bookAdvance: {
      renderer: StringRenderer,
      width: 125,
    },
    checkin: {
      renderer: StringRenderer,
      width: 125,
    },
    checkout: {
      renderer: StringRenderer,
      width: 125,
    },
    stayLength: {
      renderer: StringRenderer,
      width: 125,
    },
    totalPax: {
      renderer: StringRenderer,
      width: 125,
    },
    adultCount: {
      renderer: StringRenderer,
      width: 125,
    },
    kidCount: {
      renderer: StringRenderer,
      width: 125,
    },
    availableRooms: {
      renderer: StringRenderer,
      width: 125,
    },
    globalRestriction: {
      renderer: StringRenderer,
      width: 125,
    },
    noAvailability: {
      renderer: NumberRenderer,
      width: 125,
    },
    minPrice: {
      renderer: NumberRenderer,
      width: 125,
    },
    avgPrice: {
      renderer: NumberRenderer,
      width: 125,
    },
    maxPrice: {
      renderer: NumberRenderer,
      width: 125,
    },
    currency: {
      renderer: StringRenderer,
      width: 125,
    },
    lang: {
      renderer: StringRenderer,
      width: 125,
    },
    loyaltyBrand: {
      renderer: StringRenderer,
      width: 125,
    },
    loyaltyUser: {
      renderer: StringRenderer,
      width: 125,
    },
    referrer: {
      renderer: StringRenderer,
      width: 200,
    },
    affiliate: {
      renderer: StringRenderer,
      width: 200,
    },
    origin: {
      renderer: StringRenderer,
      width: 125,
    },
    source: {
      renderer: StringRenderer,
      width: 125,
    },
    medium: {
      renderer: StringRenderer,
      width: 125,
    },
    tag: {
      renderer: StringRenderer,
      width: 125,
    },
    device: {
      renderer: StringRenderer,
      width: 125,
    },
    funnel: {
      renderer: StringRenderer,
      width: 125,
    },
    inventoryTicker: {
      renderer: StringRenderer,
      width: 200,
    },
    resMulti: {
      renderer: StringRenderer,
      width: 200,
    },
    resTotal: {
      renderer: NumberRenderer,
      width: 125,
    },
    extrasTotal: {
      renderer: NumberRenderer,
      width: 125,
    },
    extrasQuantity: {
      renderer: StringRenderer,
      width: 125,
    },
    mandatoryExtras: {
      renderer: StringRenderer,
      width: 125,
    },
    resUnits: {
      renderer: StringRenderer,
      width: 125,
    },
    resPax: {
      renderer: StringRenderer,
      width: 125,
    }
  };
}

export function useReportQueriesFilters(): TableFilters<
  ReportQueriesFilters,
  ReportQueriesFilters
> {
  const intl = useIntl();
  return useMemo((): TableFilters<ReportQueriesFilters, ReportQueriesFilters> => {
    return {
      period: {
        filterFn: "async",
        widget: dateFilter("period", intl),
      },
    };
  }, [intl]);
}

export function generateReportQueriesRequest({
  filterValues,
  pageSize,
  currentPage,
}): GetFiltersRequestProps {
  return {
    filters: {
      startDate: filterValues?.period?.startDate
        ? `${asUTCTime(parseISO(filterValues.period.startDate), "yyyy-MM-dd")}T00:00:00Z`
        : defaultRequest.startDate,
      endDate: filterValues?.period?.endDate
        ? `${asUTCTime(parseISO(filterValues.period.endDate), "yyyy-MM-dd")}T23:59:59Z`
        : defaultRequest.endDate,
    },
    size: pageSize,
    page: currentPage,
  };
}

export function generateReportQueriesCsv(report: EventEntity[]): string {
  const headers = generateEventHeaders();
  const cellDefinitions = Object.keys(headers);
  const data = report.map(event => {
    const rowValues = cellDefinitions.map(col => {
      const value = event[col];
      if (col === "createdAt" && value instanceof Date) {
        return value.toISOString();
      }
      if(col === "inventoryTicker" && value) {
        return value.replace(/,/g, " ");
      }
      return value != null ? String(value) : "";
    });
    return rowValues.join(",");
  });
  const cellToCSV = cellDefinitions.map(it => reportQueriesMessages[it] ? formatMessage(reportQueriesMessages[it], "en") : it)
  const rows = [cellToCSV.join(","), ...data].join("\n");
  return URL.createObjectURL(new Blob([rows], { type: "text/csv" }));
}
